// BottomBar (ux/12 §5.1, ADR-0025) — единственный код, который рисует полосу действия и трогает кнопки Telegram.
// Экран при входе кладёт описание {main, secondary, back}, окно — своё поверх, при уходе описание снимается (стек).
// Нативные обработчики снимаются offClick при каждой смене: иначе они копятся, и один тап срабатывает дважды.
// Защита от двойного нажатия (ux/12 §5.3): пока действие не ответило, повторный тап игнорируется.
import { signal } from '@preact/signals';
import type { TgWebApp } from '../telegram/webapp.js';
import type { Navigator } from './navigator.js';
import type { IconName } from '../assets/icons.js';

export interface ActionButton {
  text: string;
  onClick: () => unknown;
  /** Неактивна; причина пишется в тексте («Нет связи», «Нужен 12 уровень»), ux/12 §5.2. */
  disabled?: boolean;
  tone?: 'accent' | 'outline' | 'danger';
  testId?: string;
  /** Иконка после текста («В бой · 10» + энергия): narrative/03 §9 — значок, не эмодзи. */
  icon?: IconName;
  /** Мини-портрет перед текстом: ID ассета (FIG-B01) — «В бой» на SCR-HOME (§6 п. 3). */
  portrait?: string;
}

export interface ActionDesc {
  main?: ActionButton;
  secondary?: ActionButton;
  /** Своя «‹» слева: L3 и формы (ux/02 §2). */
  back?: boolean;
  /** Скрыть полосу (сцены). */
  hidden?: boolean;
}

interface Entry { id: number; desc: ActionDesc }

const keyOf = (b: ActionButton) => b.testId ?? b.text;

export class BottomBar {
  private stack: Entry[] = [];
  private seq = 0;
  /** Ключ кнопки — testId или текст: описание пересоздаётся при каждой отрисовке экрана. */
  private pendingSet = new Set<string>();
  /** Версия для перерисовки DOM-полосы. */
  readonly version = signal(0);

  push(desc: ActionDesc): number {
    const id = ++this.seq;
    this.stack.push({ id, desc });
    this.bump();
    return id;
  }

  update(id: number, desc: ActionDesc): void {
    const e = this.stack.find((x) => x.id === id);
    if (!e) return;
    e.desc = desc;
    this.bump();
  }

  remove(id: number): void {
    this.stack = this.stack.filter((x) => x.id !== id);
    this.bump();
  }

  current(): ActionDesc | null {
    return this.stack.at(-1)?.desc ?? null;
  }

  depth(): number { return this.stack.length; }

  isPending(b: ActionButton): boolean { return this.pendingSet.has(keyOf(b)); }

  /** Один тап — один запрос: повтор до ответа игнорируется. */
  async press(b: ActionButton): Promise<void> {
    const k = keyOf(b);
    if (b.disabled || this.pendingSet.has(k)) return;
    const r = b.onClick();
    if (!(r instanceof Promise)) return;
    this.pendingSet.add(k);
    this.bump();
    try { await r; } catch { /* ошибку показывает сетевой слой */ } finally {
      this.pendingSet.delete(k);
      this.bump();
    }
  }

  private bump() { this.version.value++; }
}

/**
 * Кнопки Telegram по состоянию навигации (ux/02 §3–§4, ux/12 §5.2): BackButton — по карте Back,
 * SettingsButton — везде, кроме боя и SCR-BANNED. Возвращает функцию синхронизации: вызывать при смене экрана.
 */
export function bindTelegramButtons(wa: TgWebApp | null, nav: Navigator, onSettings: () => void) {
  let backCb: (() => void) | null = null;
  let settingsCb: (() => void) | null = null;
  return function sync(): void {
    const back = wa?.BackButton;
    if (back) {
      if (backCb) back.offClick(backCb);
      backCb = () => { nav.back(); };
      back.onClick(backCb);
      if (nav.backVisible()) back.show(); else back.hide();
    }
    const st = wa?.SettingsButton;
    if (st) {
      if (settingsCb) st.offClick(settingsCb);
      settingsCb = onSettings;
      st.onClick(settingsCb);
      const s = nav.state;
      const top = nav.top();
      const hide = s.system?.id === 'SCR-BANNED' || (!s.system && (top === 'SCR-FIGHT')) || s.service.length > 0;
      if (hide) st.hide(); else st.show();
    }
  };
}
